import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Receipt,
  Shield,
  ShieldAlert,
} from "lucide-react";
import { api } from "../api";
import { Card, PageHeader, cx } from "../ui";

type ComplianceData = {
  units: Array<{
    id: string;
    unitNumber: string;
    buildingId: string;
    buildingName: string;
  }>;
  insurance: Array<{
    unitId: string;
    unitNumber: string;
    buildingName: string;
    hasPolicy: boolean;
    carrierName: string | null;
    policyNumber: string | null;
    expirationDate: string | null;
    validationStatus: string | null;
    coveredToday: boolean;
  }>;
  fees: Array<{
    id: string;
    unitId: string | null;
    projectId: string | null;
    kind: string;
    description: string;
    amountCents: number;
    status: string;
    dueAt: string;
    createdAt: string;
  }>;
  fines: Array<{
    id: string;
    unitId: string | null;
    projectId: string | null;
    reasonCode: string;
    description: string;
    amountCents: number;
    periodMonth: string | null;
    leviedAt: string;
    waivedAt: string | null;
    paidAt: string | null;
  }>;
  totals: {
    outstandingFeesCents: number;
    outstandingFinesCents: number;
    totalOwedCents: number;
    uncoveredUnitCount: number;
  };
  owedByKind: Array<{
    kind: string;
    count: number;
    amountCents: number;
  }>;
};

const KIND_LABEL: Record<string, string> = {
  security_deposit: "Construction security deposit",
  arch_construction: "Architectural review fee",
  riser_shutdown: "Riser shutdown",
  riser_reschedule: "Riser shutdown reschedule",
  telecom_shutdown: "Telecom shutdown",
  missing_insurance_fine: "Missing-insurance fine",
  late_fee: "Late fee",
  returned_check: "Returned check",
  other: "Other charge",
};

const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function MyCompliancePage() {
  const [data, setData] = useState<ComplianceData | null>(null);

  useEffect(() => {
    void api
      .get<ComplianceData>("/api/my/compliance")
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <div className="max-w-4xl">
        <PageHeader title="Compliance" />
        <Card>
          <p className="text-ink-500">Loading…</p>
        </Card>
      </div>
    );
  }

  const allClear =
    data.totals.totalOwedCents === 0 && data.totals.uncoveredUnitCount === 0;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Compliance"
        subtitle="Everything you owe across every unit and project — insurance, fees, fines, and how to clear them."
      />

      {/* Headline */}
      <Card>
        {allClear ? (
          <div className="flex items-center gap-3">
            <CheckCircle2
              size={28}
              className="text-status-approved-fg flex-shrink-0"
            />
            <div>
              <h2 className="text-ink-800 m-0">All clear</h2>
              <p className="text-[13px] text-ink-500 mt-1">
                No outstanding fees, no missing-insurance fines, all your units
                are covered. Nice.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat
              label="Total owed"
              value={dollars(data.totals.totalOwedCents)}
              tone={data.totals.totalOwedCents > 0 ? "warn" : "ok"}
            />
            <Stat
              label="Outstanding fees"
              value={dollars(data.totals.outstandingFeesCents)}
              hint={`${data.fees.length} item${data.fees.length === 1 ? "" : "s"}`}
            />
            <Stat
              label="Uncovered units"
              value={`${data.totals.uncoveredUnitCount}`}
              tone={data.totals.uncoveredUnitCount > 0 ? "rejected" : "ok"}
              hint={
                data.totals.uncoveredUnitCount > 0
                  ? "Add or renew on Insurance"
                  : "All units have a policy on file"
              }
            />
          </div>
        )}
      </Card>

      {/* Insurance per unit */}
      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Shield size={18} /> Insurance by unit
        </h2>
        {data.insurance.length === 0 ? (
          <p className="text-ink-500 text-sm">
            You aren't currently listed as an owner on any unit. Use{" "}
            <Link to="/claim-unit" className="text-brand-700">
              Claim a unit
            </Link>{" "}
            if that's wrong.
          </p>
        ) : (
          <ul className="divide-y divide-sand-100">
            {data.insurance.map((row) => {
              const expDate = row.expirationDate
                ? new Date(row.expirationDate)
                : null;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const daysToExpiry = expDate
                ? Math.ceil((expDate.getTime() - today.getTime()) / 86_400_000)
                : null;
              const expiringSoon =
                row.coveredToday &&
                daysToExpiry !== null &&
                daysToExpiry <= 30;
              return (
                <li
                  key={row.unitId}
                  className="py-3 flex items-start justify-between gap-3 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-800">
                      Unit {row.unitNumber}
                      <span className="ml-2 text-[12px] font-normal text-ink-500">
                        {row.buildingName}
                      </span>
                    </div>
                    {row.hasPolicy ? (
                      <div className="text-[12.5px] text-ink-600 mt-0.5">
                        {row.carrierName} · {row.policyNumber} · expires{" "}
                        {row.expirationDate}
                        {expiringSoon && daysToExpiry !== null && (
                          <span className="ml-2 text-status-review-fg font-medium">
                            ({daysToExpiry}d left)
                          </span>
                        )}
                        {!row.coveredToday && (
                          <span className="ml-2 text-status-rejected-fg font-medium">
                            EXPIRED
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-[12.5px] text-status-rejected-fg mt-0.5 inline-flex items-center gap-1">
                        <AlertTriangle size={12} />
                        No policy on file. The $100/mo missing-insurance fine
                        starts on the 15th of the month following any lapse.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {row.coveredToday ? (
                      <span className="badge bg-status-approved-bg text-status-approved-fg">
                        covered
                      </span>
                    ) : (
                      <span className="badge bg-status-rejected-bg text-status-rejected-fg">
                        not covered
                      </span>
                    )}
                    <Link
                      to="/my/insurance"
                      className="inline-flex items-center px-2.5 py-1 text-[12px] text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md no-underline hover:no-underline"
                    >
                      {row.hasPolicy ? "Renew" : "Add policy"}
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Outstanding fees + breakdown */}
      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Receipt size={18} /> Outstanding fees
        </h2>
        {data.fees.length === 0 ? (
          <p className="text-ink-500 text-sm">
            No outstanding fees. Property Management will mark each item
            received once your check is in hand.
          </p>
        ) : (
          <>
            {data.owedByKind.length > 1 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {data.owedByKind.map((b) => (
                  <div
                    key={b.kind}
                    className="bg-sand-50 border border-sand-100 rounded-md px-3 py-2"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-ink-400">
                      {KIND_LABEL[b.kind] ?? b.kind}
                    </div>
                    <div className="text-ink-800 font-semibold text-[14px]">
                      {dollars(b.amountCents)}
                    </div>
                    <div className="text-[11px] text-ink-500">
                      {b.count} item{b.count === 1 ? "" : "s"}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ul className="divide-y divide-sand-100">
              {data.fees.map((f) => (
                <li
                  key={f.id}
                  className="py-2.5 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-ink-800">
                      {KIND_LABEL[f.kind] ?? f.kind}
                      <span className="ml-2 text-[12px] font-normal text-ink-500">
                        {f.description}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5">
                      Due {new Date(f.dueAt).toLocaleDateString()}
                      {f.projectId && (
                        <Link
                          to={`/projects/${f.projectId}`}
                          className="ml-2 text-brand-700"
                        >
                          → project
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="text-ink-800 font-semibold">
                    {dollars(f.amountCents)}
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11.5px] text-ink-500 mt-3 leading-snug">
              Pay by check to{" "}
              <em>"The Elm at Clark Condominium Association"</em>. Property
              Management marks each row received once the check is in hand.
            </p>
          </>
        )}
      </Card>

      {/* Fines history */}
      {data.fines.length > 0 && (
        <Card>
          <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
            <ShieldAlert size={18} /> Fines history
          </h2>
          <ul className="divide-y divide-sand-100">
            {data.fines.map((f) => {
              const cleared = f.waivedAt || f.paidAt;
              return (
                <li
                  key={f.id}
                  className="py-2.5 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-ink-800">
                      {f.description}
                    </div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5">
                      Levied {new Date(f.leviedAt).toLocaleDateString()}
                      {f.periodMonth && ` · for ${f.periodMonth}`}
                      {f.waivedAt &&
                        ` · waived ${new Date(f.waivedAt).toLocaleDateString()}`}
                      {f.paidAt &&
                        ` · paid ${new Date(f.paidAt).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-ink-800 font-semibold">
                      {dollars(f.amountCents)}
                    </div>
                    <span
                      className={cx(
                        "badge",
                        cleared
                          ? "bg-status-approved-bg text-status-approved-fg"
                          : "bg-status-rejected-bg text-status-rejected-fg",
                      )}
                    >
                      {f.waivedAt
                        ? "waived"
                        : f.paidAt
                          ? "paid"
                          : "outstanding"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "warn" | "rejected";
}) {
  return (
    <div
      className={cx(
        "rounded-md border px-4 py-3",
        tone === "rejected"
          ? "bg-status-rejected-bg border-status-rejected-fg/30"
          : tone === "warn"
            ? "bg-status-review-bg border-status-review-fg/30"
            : "bg-sand-50 border-sand-100",
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-500">
        {label}
      </div>
      <div
        className={cx(
          "text-[18px] font-semibold mt-0.5",
          tone === "rejected"
            ? "text-status-rejected-fg"
            : tone === "warn"
              ? "text-status-review-fg"
              : "text-ink-800",
        )}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[11.5px] text-ink-500 mt-0.5 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}
